import { useState } from 'react'
import { PexIcon } from '../../components/icons/PexIcon'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Field } from '../../components/ui/field'
import { Select } from '../../components/ui/select'
import { useT } from '../../i18n'
import { SUPPLY_WORK_ORDERS_STRINGS } from '../../i18n/supply/work-orders'

type SupplyRoleOption = {
  value: string
  label: string
}

type SupplyWorkOrdersRoleStateProps = {
  currentPersona: string
  simulation: boolean
  options: SupplyRoleOption[]
  onSwitch: (value: string) => void
  onBack: () => void
}

export function SupplyWorkOrdersRoleState({
  currentPersona,
  simulation,
  options,
  onSwitch,
  onBack,
}: SupplyWorkOrdersRoleStateProps) {
  const t = useT(SUPPLY_WORK_ORDERS_STRINGS).myWorkOrders.roleState
  const [selectedPersona, setSelectedPersona] = useState(
    () => options[0]?.value ?? '',
  )

  return (
    <div
      className="flex min-h-[min(36rem,65vh)] items-center justify-center py-8"
      data-testid="supply-work-orders-role-state"
    >
      <Card className="w-full max-w-xl overflow-hidden">
        <div className="h-1 bg-[var(--color-action)]" />
        <div className="px-6 py-7 sm:px-8 sm:py-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-panel)] bg-[var(--color-surface-muted)] text-[var(--color-text)]">
            <PexIcon name="factory" size="lg" weight="duotone" decorative />
          </div>

          <h1 className="mt-5 text-xl font-semibold tracking-[-0.02em] text-[var(--color-text)]">
            {t.title}
          </h1>
          <p className="mt-2 max-w-lg text-sm leading-6 text-[var(--color-text-muted)]">
            {t.description(currentPersona)}
          </p>

          <div className="mt-5 flex items-center gap-2 rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-xs text-[var(--color-text-muted)]">
            <PexIcon name="account" size="sm" tone="muted" decorative />
            <span className="font-semibold text-[var(--color-text)]">
              {t.currentRole}:
            </span>
            <span className="truncate">{currentPersona}</span>
          </div>

          {simulation ? (
            <div className="mt-6 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <Field
                id="supply-work-orders-role-persona"
                label={t.productionPartner}
              >
                <Select
                  value={selectedPersona}
                  onChange={(event) => setSelectedPersona(event.target.value)}
                >
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Button
                type="button"
                variant="primary"
                disabled={!selectedPersona}
                onClick={() => onSwitch(selectedPersona)}
              >
                <PexIcon
                  name="caretRight"
                  size="sm"
                  tone="on-emphasis"
                  className="mr-1.5"
                  decorative
                />
                {t.openWorkspace}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="primary"
              className="mt-6"
              onClick={onBack}
            >
              {t.backToOverview}
            </Button>
          )}
        </div>
      </Card>
    </div>
  )
}
